<template>
  <div class="route-container">
    <AgGridVue  
      class="app-table ag-theme-alpine"
      :columnDefs="columnDefs"
      :rowData="rowData"
      @grid-ready="onGridReady"
      rowSelection="single"
      :sideBar="sideBar"
      :masterDetail="true"
      :keepDetailRowsCount="1"
      :groupDefaultExpanded="1"
      :autoGroupColumnDef="autoGroupColumnDef"
      :suppressAggFuncInHeader="true"
      @row-clicked="onRowClicked"
      :detailCellRendererParams="detailCellRendererParams"
      :getRowHeight="getRowHeight"
      :embedFullWidthRows="false"
      :rowClassRules="rowClassRules"
      :icons="icons"
    >
    </AgGridVue>
  </div>
</template>

<script>
import ControllingService from "../services/ControllingService";
import GridService from "../services/GridService";
import EventBus from '@/services/EventBus.js'

import { AgGridVue } from "ag-grid-vue3";
import 'ag-grid-enterprise';

import CurrencyCellRenderer from "./CellRenderer/CurrencyCellRenderer";
import DateCellRenderer from "./CellRenderer/DateCellRenderer";
import PercentageCellRenderer from "./CellRenderer/PercentageCellRenderer";
import SurveyInstanceCountCellRenderer from "./CellRenderer/SurveyInstanceCountCellRenderer";
import WeightedBudgetCellRenderer from "./CellRenderer/WeightedBudgetCellRenderer";

import ConfigurationToolPanel from "./ConfigurationToolPanel";
import DetailColumnToolPanel from "./DetailColumnToolPanel";

export default {
  name: "Archive",
  components: {
    AgGridVue,
    ConfigurationToolPanel,
    currencyCellRenderer: CurrencyCellRenderer,
    dateCellRenderer: DateCellRenderer,
    DetailColumnToolPanel,
    percentageCellRenderer:PercentageCellRenderer,
    surveyInstanceCountCellRenderer: SurveyInstanceCountCellRenderer,
    weightedBudgetCellRenderer: WeightedBudgetCellRenderer,
  },
  data() {
    return {
      autoGroupColumnDef: {},
      columnDefs: null,
      detailCellRendererParams: null,
      getRowHeight: null,
      gridApi: null,
      gridColumnApi: null,
      icons:{
        cog: "<i class='cog-via-css'/>"
      },
      rowClassRules: {
        'first-row': params => params.rowIndex === 0,
      },
      rowData: null,
      sideBar: {
        toolPanels: [
          {
            id: 'configuration',
            labelDefault: 'Konfiguration',
            labelKey: 'configuration',
            iconKey: 'cog',
            toolPanel: 'ConfigurationToolPanel',
            toolPanelParams: {
              parentName: this.$options.name,
            },
          },
          {
            id: 'columns',
            labelDefault: 'Spalten - Unterprojekt',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false,
            },
          },
          {
            id: 'detailColumns',
            labelDefault: 'Spalten - Auftrag',
            labelKey: 'detailColumns',
            iconKey: 'columns',
            toolPanel: 'DetailColumnToolPanel',
          },
          {
            id: 'filters',
            labelDefault: 'Filter',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
            toolPanelParams: {
              suppressExpandAll: false,
              suppressFilterSearch: false,
            },
          },
        ],
      },
      selectedRows: null,
      selectedYear: null,
    };
  },
  created() {
    this.getRowHeight = (params) => {
      if (params.node?.detail && params.data?.Orders) {
        var offset = 80;
        var allDetailRowHeight =
          (params.data.Orders.length <= 10 ? params.data.Orders.length : 10) *
          params.api.getSizesForCurrentTheme().rowHeight;
        var gridSizes = params.api.getSizesForCurrentTheme();
        return (
          allDetailRowHeight +
          ((gridSizes && gridSizes.headerHeight) || 0) +
          offset
        );
      }
    };
  },
  beforeMount() {
    this.selectedYear = new Date().getFullYear();
    this.columnDefs = [
      { headerName: "AggregationGroup", field: 'AggregationGroup', rowGroup: true, hide: true, suppressColumnsToolPanel: true},
      { headerName: "Kunde", field: "ClientName", sortable: true, filter: true, width:200, resizable: true },
      { headerName: "Projekt", field: "ProjectName", sortable: true, filter: true, width:250, resizable: true },
      { headerName: "Unterprojekt", field: "SubProjectName", sortable: true, filter: true, width:150, resizable: true },
      { headerName: "CL", field: "Consultant", sortable: true, filter: true, resizable: true, width:70 },
      { headerName: "Budget", field: "OrderBudget", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Wahrscheinlichkeit",field: "Probability", hide: true, sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'percentageCellRenderer', 
        aggFunc: params => {
          let total = 0;
          params.values.forEach(value => total += value);
          return (total / params.values.length).toFixed(0);
        }  
      },
      { headerName: "Rückstellungen", field: "Reserve", hide: true, sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' }, 
      { headerName: "Budget abz. Rückstellungen", field: "BudgetMinusReserve", hide: true, sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' }, 
      { headerName: "G&G in €", field: "OverheadCosts", cellRenderer: "currencyCellRenderer", sortable: true, filter: true, width:150, resizable: true, aggFunc: 'sum' },
      { headerName: "G&G in %", field: "OverheadCharge", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'percentageCellRenderer',
        aggFunc: params => {
          let total = 0;
          params.values.forEach(value => total += value);
          return (total / params.values.length).toFixed(0);
        }  
      },
      { headerName: "Vertriebsaufschlag in €", field:"MarketingCosts", cellRenderer: "currencyCellRenderer", sortable: true, filter: true, width:150, resizable: true, aggFunc: 'sum' },
      { headerName: "Vertriebsaufschlag in %", field:"MarketingCharge", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'percentageCellRenderer',
        aggFunc: params => {
          let total = 0;
          params.values.forEach(value => total += value);
          return (total / params.values.length).toFixed(0);
        }  
      },
      { headerName: "Fremdkosten MSM", field: "ExternalCostsMsmTk", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Fremdkosten Agentur", field: "ExternalCostsAgency", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Fremdkosten BOLD", field: "ExternalCostsMdatiqs", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Fremdkosten BOLD 2€ pro Check", field: "SurveyInstancePriceMdatiqs", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Fremdkosten Sonstiges", field: "ExternalCostsOther", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "DB1", field:"ContributionMargin", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Verfügbares Abteilungsbudget", field: "DepartmentBudget", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Budget Feld", field: "BudgetField", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Restbudget Feld", field: "RemainingBudgetField", sortable: true, filter: true, width:180, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Budget PM", field: "BudgetProjectManagement", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Restbudget PM", field: "RemainingBudgetProjectManagement", sortable: true, filter: true, width:180, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Budget QSS", field: "BudgetQualityAssurance", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Restbudget QSS", field: "RemainingBudgetQualityAssurance", sortable: true, filter: true, width:180, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Verbleibendes Budget", field: "RemainingBudget", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer', aggFunc: 'sum' },
      { headerName: "Erfüllungsrate Checks", field: "SurveyInstanceCount", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'surveyInstanceCountCellRenderer',
        aggFunc: params => {
          let totalSurveyInstanceCount = 0;
          let totalPlannedSurveyInstanceNumber = 0;
          params.values.forEach(sic => {
            if(sic && sic.length) {
              if(sic[0].PlannedSurveyInstanceNumber) totalPlannedSurveyInstanceNumber += sic[0].PlannedSurveyInstanceNumber;
              sic.forEach( e => {
                if(e) {
                  totalSurveyInstanceCount += e.SurveyInstanceCount;
                }
              })
            }
          })
          return [{"SurveyInstanceCount":totalSurveyInstanceCount, "PlannedSurveyInstanceNumber": totalPlannedSurveyInstanceNumber}];
        }  
      },
    ];
  },
  mounted() {
    this.detailCellRendererParams = {
      // level 2 grid options
      detailGridOptions: {
        columnDefs: [
          { headerName: "Auftrag", field: "OrderName", sortable: true, filter: true, width:150, resizable: true },
          { headerName: "Kostenstelle", field: "FullCostCenterCode", sortable: true, filter: true, width: 130, resizable: true },
          { headerName: "CL", field: "Consultant", sortable: true, filter: true, width:70, resizable: true },
          { headerName: "Startdatum", field: "StartDate", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'dateCellRenderer' },
          { headerName: "Enddatum", field: "EndDate", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'dateCellRenderer' },
          { headerName: "Budget", field: "Budget", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Rückstellungen", field: "Reserve", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Budget abz. Rückstellungen", field: "BudgetMinusReserve", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer' }, 
          { headerName: "G&G in €", field: "OverheadCosts", cellRenderer: "currencyCellRenderer", sortable: true, filter: true, width:150, resizable: true, aggFunc: 'sum' },
          { headerName: "G&G in %", field: "OverheadCharge", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'percentageCellRenderer' },
          { headerName: "Vertriebsaufschlag in €", field:"MarketingCosts", cellRenderer: "currencyCellRenderer", sortable: true, filter: true, width:150, resizable: true, aggFunc: 'sum' },
          { headerName: "Vertriebsaufschlag in %", field:"MarketingCharge", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'percentageCellRenderer' },
          { headerName: "Fremdkosten MSM", field: "ExternalCostsMsmTk", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Fremdkosten Agentur", field: "ExternalCostsAgency", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Fremdkosten BOLD", field: "ExternalCostsMdatiqs", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Fremdkosten BOLD 2€ pro Check", field: "SurveyInstancePriceMdatiqs", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Fremdkosten Sonstiges", field: "ExternalCostsOther", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "DB1", field:"ContributionMargin", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Verfügbares Abteilungsbudget", field: "DepartmentBudget", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Budget Feld", field: "BudgetField", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Restbudget Feld", field: "RemainingBudgetField", sortable: true, filter: true, width:180, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Budget PM", field: "BudgetProjectManagement", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Restbudget PM", field: "RemainingBudgetProjectManagement", sortable: true, filter: true, width:180, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Budget QSS", field: "BudgetQualityAssurance", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Restbudget QSS", field: "RemainingBudgetQualityAssurance", sortable: true, filter: true, width:180, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Verbleibendes Budget", field: "RemainingBudget", sortable: true, filter: true, width:200, resizable: true, cellRenderer: 'currencyCellRenderer' },
          { headerName: "Erfüllungsrate Checks", field: "SurveyInstanceCount", sortable: true, filter: true, width:150, resizable: true, cellRenderer: 'surveyInstanceCountCellRenderer' },
        ],
        rowSelection: 'single',
      },
      getDetailRowData: (params) => {
        params.successCallback(params.data.Orders);
      },
    };
    // listen to event from DetailColumnToolPanel to show/hide level 2 columns
    EventBus.on('DetailColumnHide', this.onDetailColumnHide);
    EventBus.on('DetailColumnHideAll', this.onDetailColumnHideAll);
    EventBus.on(`${this.$options.name}_newConfig`, this.onNewConfig);
  },
  activated() {
    if (this.$route.path == "/archive") {
      this.loadRowData();
    }
  },
  methods: {
    onDetailColumnHide(field, hide) {
      var def = this.detailCellRendererParams.detailGridOptions.columnDefs.find(def => def.field === field);
      def.hide = hide;
      this.gridApi.onGroupExpandedOrCollapsed();
    },
    onDetailColumnHideAll() {
      this.detailCellRendererParams.detailGridOptions.columnDefs.forEach(e => e.hide = hide);
      this.gridApi.onGroupExpandedOrCollapsed();
    },
    onNewConfig(params) {
      this.selectedYear = params.selectedYear;
      this.loadRowData();
      GridService.clearFilter(this.gridApi);
    },
    loadRowData() {
      if(this.gridApi) this.gridApi.showLoadingOverlay();

      ControllingService.getSubprojectAndOrder(null, true, null, null, this.selectedYear)
      .then(res => {
        this.rowData = [];
        if(res.data === '') return;
        this.rowData = res.data;

        // append attribute used in SelectForAggregatedRowCellRenderer for aggregation of selection
        this.rowData.forEach(e => {
          e.isSelectedForAggregation = false;
          e.AggregationGroup = 'Gesamt';
        });
      })
      .finally(() => {
        if(this.gridApi) this.gridApi.hideOverlay();
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      this.gridColumnApi.setColumnWidth('ag-Grid-AutoColumn', 140);

    },
    onRowClicked() {
      // prevent for first row, aggregation row
      if(this.gridApi.getFocusedCell().rowIndex === 0) {return;}

      // collapse other rows on selectionchange
      this.gridApi.forEachNode(function (node) {
        if(node.rowIndex === 0) {return};
        if(node.selected === true) {
          node.expanded = !node.expanded;
        } else {
          node.expanded = false;
        }
      });
      this.gridApi.onGroupExpandedOrCollapsed();
    },
  },
}
</script>

<style>
  .first-row{
    font-weight: bold;
    background-color: #52688d31 !important;
  }
</style>